import React from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { Row, InputGroup, Glyphicon, Modal } from 'react-bootstrap'
import { split, replace, join } from 'lodash'
import LanguageProvider from '../../containers/LanguageProvider/LanguageProvider'
import messages from './messages'
import ProductNameHOC from '../../hocs/ProductNameHOC'
import { OptionsHOC } from '../../hocs'
import SEO from '../SEO'
import { ASSETS_URL } from '../../constants/urls'
import { useAppContext } from '../../containers/App/Context'
import { ActiveFilterStyle, DeviceFaceplateStyle, StyledModal } from './styles'
import CommonDropdown from '../Dropdown'
import { DeviceCard, SearchField } from '../'

const proptypes = {
  show: PropTypes.bool.isRequired,
  setDevice: PropTypes.func,
  getOption: PropTypes.func.isRequired
}

const DeviceFaceplateNamePretty = ({ device, setDevice, filter }) => {
  const regExp = /\(|\)/g
  const [deviceName, ...rest] = split(device.name_pretty, ' (')
  const deviceSubName = replace(join(rest, ' '), regExp, '')

  return (
    <DeviceFaceplateStyle>
      <DeviceCard
        onClick={() =>
          setDevice(String(device.id), device.name_pretty, filter, device.slug)
        }
        imgURL={ASSETS_URL + device.image_location}
        altTag={device.name_pretty || ''}
        deviceName={deviceName}
        subName={deviceSubName}
      />
    </DeviceFaceplateStyle>
  )
}

const ActiveFilter = ({ filterType, filterTerm, styles, remove }) => {
  if (filterTerm) {
    return (
      <ActiveFilterStyle
        onClick={() => {
          remove({ filterType: filterType, filterTerm: '' })
        }}
      >
        <div>
          <span>{filterTerm}</span>
          <InputGroup.Addon aria-label={`click to remove ${filterType}`}>
            <Glyphicon glyph="remove-circle" />
          </InputGroup.Addon>
        </div>
      </ActiveFilterStyle>
    )
  } else return null
}

const FilterContainerPadding = styled.div`
  padding: 0 20px;
`

const FiltersContainerAlignment = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  --search-input-height: 46px;
  --search-input-width: var(--filter-width);

  form {
    margin: 0px;
  }

  @media screen and (max-width: 767px) {
    flex-direction: column;
    --filter-width: 100%;
  }
`

const ActiveFilterContainerFlex = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`

const Devices = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;

  @media screen and (max-width: 767px) {
    justify-content: center;
  }
`

function DeviceSelectModal({
  createUserEvent,
  devices,
  setDevice,
  show,
  language,
  manufacturers,
  deviceSoftware,
  productNameSingular,
  productTranslation,
  getOption,
  onHide
}) {
  const [searchTerm, setSearchTerm] = React.useState('')
  const [manufacturerFilter, setManufacturerFilter] = React.useState('')
  const [osFilter, setOsFilter] = React.useState('')
  const [styleFilter, setStyleFilter] = React.useState('')
  const translated = messages[language]
  const disableFilters = getOption('disableProductFilters') || false
  const { versionKey: version } = useAppContext()

  const title = productNameSingular
    ? translated['title'].replace(productTranslation, productNameSingular)
    : translated['title']

  const updateFilter = filterObj => {
    switch (filterObj.filterType) {
      case 'clear':
        setSearchTerm('')
        setManufacturerFilter('')
        setOsFilter('')
        setStyleFilter('')
        break
      case 'manufacturer':
        createUserEvent({
          category: 'deviceselector',
          data: {
            action: 'updateFilter',
            filter: {
              manufacturer: filterObj.filterTerm,
              os: osFilter,
              style: styleFilter,
              search: searchTerm
            }
          }
        })
        setManufacturerFilter(filterObj.filterTerm)
        break
      case 'os':
        createUserEvent({
          category: 'deviceselector',
          data: {
            action: 'updateFilter',
            filter: {
              manufacturer: manufacturerFilter,
              os: filterObj.filterTerm,
              style: styleFilter,
              search: searchTerm
            }
          }
        })
        setOsFilter(filterObj.filterTerm)
        break
      case 'style':
        createUserEvent({
          category: 'deviceselector',
          data: {
            action: 'updateFilter',
            filter: {
              manufacturer: manufacturerFilter,
              os: osFilter,
              style: filterObj.filterTerm,
              search: searchTerm
            }
          }
        })
        setStyleFilter(filterObj.filterTerm)
        break
      case 'search':
        createUserEvent({
          category: 'deviceselector',
          data: {
            action: 'updateFilter',
            filter: {
              manufacturer: manufacturerFilter,
              os: osFilter,
              style: styleFilter,
              search: filterObj.filterTerm
            }
          }
        })
        setSearchTerm(filterObj.filterTerm)
        break
      default:
    }
  }
  const handleSearch = e => {
    updateFilter({ filterType: 'search', filterTerm: e })
  }

  const applyFilter = () => {
    let deviceList = devices
    deviceList = deviceList.filter(device => {
      return (
        manufacturerFilter === '' || device.manufacturer === manufacturerFilter
      )
    })
    deviceList = deviceList.filter(device => {
      return osFilter === '' || device.os === osFilter
    })
    deviceList = deviceList.filter(device => {
      let searchTermLower = searchTerm.toLowerCase()
      if (searchTermLower === '5g') {
        searchTermLower = ' 5g'
      }
      return (
        searchTermLower === '' ||
        device.manufacturer.toLowerCase().includes(searchTermLower) ||
        (device.os && device.os.toLowerCase().includes(searchTermLower)) ||
        device.name_pretty.toLowerCase().includes(searchTermLower) ||
        device.id.toString().includes(searchTermLower) ||
        device.customerID.toLowerCase().includes(searchTermLower)
      )
    })
    return deviceList
  }
  const deviceList = applyFilter()

  return (
    <StyledModal
      show={show}
      onHide={() => onHide()}
      bsSize="lg"
      dialogClassName="db-font-override animated fadeIn"
      role="dialog"
      aria-labelledby="Device selector modal"
      version={version}
    >
      <SEO
        title={
          productNameSingular
            ? translated['title'].replace(
                productTranslation,
                productNameSingular
              )
            : translated['title']
        }
      />
      <Modal.Header closeButton className="borderless">
        <Modal.Title componentClass="h1" id="contained-modal-title-sm">
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <FilterContainerPadding>
            <FiltersContainerAlignment disableFilters={disableFilters}>
              {!disableFilters && (
                <>
                  <CommonDropdown
                    size="full"
                    label={translated['brandfilter']}
                    options={manufacturers.map(manufacturer => ({
                      label: manufacturer,
                      value: manufacturer
                    }))}
                    value={manufacturerFilter}
                    onChange={e => {
                      updateFilter({
                        filterType: 'manufacturer',
                        filterTerm: e
                      })
                    }}
                    variant="secondary"
                  />
                  <CommonDropdown
                    size="full"
                    label={translated['osfilter']}
                    options={deviceSoftware.map(os => ({
                      label: os,
                      value: os
                    }))}
                    value={osFilter}
                    onChange={e =>
                      updateFilter({
                        filterType: 'os',
                        filterTerm: e
                      })
                    }
                    variant="secondary"
                  />
                </>
              )}
              <SearchField
                label={translated['search']}
                onChange={handleSearch}
                value={searchTerm}
                variant="secondary"
              />
            </FiltersContainerAlignment>
            <ActiveFilterContainerFlex>
              <ActiveFilter
                remove={updateFilter}
                filterType={'manufacturer'}
                filterTerm={manufacturerFilter}
              />
              <ActiveFilter
                remove={updateFilter}
                filterType={'os'}
                filterTerm={osFilter}
              />
              <ActiveFilter
                remove={updateFilter}
                filterType={'style'}
                filterTerm={styleFilter}
              />
              <ActiveFilter
                remove={updateFilter}
                filterType={'search'}
                filterTerm={searchTerm}
              />
            </ActiveFilterContainerFlex>
          </FilterContainerPadding>
        </Row>
        <Devices>
          {deviceList.map((device, index) => {
            return (
              <DeviceFaceplateNamePretty
                device={device}
                setDevice={setDevice}
                key={`device_${index}`}
                filter={{
                  manufacturer: manufacturerFilter,
                  os: osFilter,
                  style: styleFilter,
                  search: searchTerm
                }}
              />
            )
          })}
        </Devices>
      </Modal.Body>
    </StyledModal>
  )
}

DeviceSelectModal.propTypes = proptypes

export default LanguageProvider(OptionsHOC(ProductNameHOC(DeviceSelectModal)))



// WEBPACK FOOTER //
// ./src/components/DeviceSelectModal/DeviceSelectModal.js